import React from "react";
import { CanonicalBusinessEvent } from "../../../../types/cbes";
import { determineFlowTypeFromObjects } from "../../../../helpers/flowTypeUtils";
import { isCanonicalBusinessEvent } from "../../../../helpers/typeChecks";
import {
  AccountingEventsGroup,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../../../types";
import { Stage } from "../../../../lusca-search-api/generated-src";

type JsonDataType =
  | CanonicalBusinessEvent
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument
  | DigitalPaymentDocument;

interface CrossStageData {
  cbes: JsonDataType[];
  events: JsonDataType[];
  rawCbes?: CanonicalBusinessEvent[];
}

interface CrossStageRawData {
  totalCbes: number;
  totalEvents: number;
}

interface UseUsecaseDifferencesProps {
  crossStageEnabled: boolean;
  compareStage: Stage;
  currentStage: Stage;
  isCbe: boolean;
  isAccountingGroup: boolean;
  objects: JsonDataType[];
  crossStageData: CrossStageData;
  crossStageRawData: CrossStageRawData;
  crossStageLoading?: boolean;
}

interface UsecaseDifferencesResult {
  currentCount: number;
  compareCount: number;
  currentUsecases: string[];
  compareUsecases: string[];
  currentOnly: string[];
  compareOnly: string[];
  flowType: string;
  compareHasOtherFlowData: boolean;
  otherFlowUsecases: string[];
}

export const useUsecaseDifferences = ({
  crossStageEnabled,
  compareStage,
  currentStage,
  isCbe,
  isAccountingGroup,
  objects: filteredObjects,
  crossStageData,
  crossStageRawData,
  crossStageLoading,
}: UseUsecaseDifferencesProps): UsecaseDifferencesResult | null => {
  return React.useMemo(() => {
    if (
      !crossStageEnabled ||
      compareStage === currentStage ||
      !isCbe ||
      isAccountingGroup ||
      crossStageLoading || // Don't show differences while loading
      // Don't show differences if there's no data at all in the compare stage
      (crossStageRawData.totalCbes === 0 && crossStageRawData.totalEvents === 0)
    ) {
      return null;
    }

    const currentUsecases = new Set(
      filteredObjects
        .filter((obj): obj is CanonicalBusinessEvent =>
          isCanonicalBusinessEvent(obj)
        )
        .map((cbe) => cbe.usecaseName)
    );
    // Use rawCbes (unfiltered) for cross-stage to detect all usecases including NoOp
    const compareUsecases = new Set(
      (crossStageData.rawCbes || crossStageData.cbes)
        .filter((obj): obj is CanonicalBusinessEvent =>
          isCanonicalBusinessEvent(obj)
        )
        .map((cbe) => cbe.usecaseName)
    );

    if (
      currentUsecases.size !== compareUsecases.size ||
      ![...currentUsecases].every((uc) => compareUsecases.has(uc))
    ) {
      // Get flow type for messaging
      const flowType = determineFlowTypeFromObjects(filteredObjects);

      // Calculate if compare stage has data in other flows and get those usecases
      const compareHasOtherFlowData =
        crossStageRawData.totalCbes > crossStageData.cbes.length ||
        crossStageRawData.totalEvents > crossStageData.events.length;

      let otherFlowUsecases: string[] = [];
      if (compareHasOtherFlowData && crossStageData.rawCbes) {
        const allRawUsecases = new Set(
          crossStageData.rawCbes.map((cbe) => cbe.usecaseName)
        );
        const currentFilteredUsecases = new Set(
          crossStageData.cbes
            .filter((obj): obj is CanonicalBusinessEvent =>
              isCanonicalBusinessEvent(obj)
            )
            .map((cbe) => cbe.usecaseName)
        );
        otherFlowUsecases = [...allRawUsecases].filter(
          (usecase) => !currentFilteredUsecases.has(usecase)
        );
      }

      return {
        currentCount: currentUsecases.size,
        compareCount: compareUsecases.size,
        currentUsecases: [...currentUsecases],
        compareUsecases: [...compareUsecases],
        currentOnly: [...currentUsecases].filter(
          (uc) => !compareUsecases.has(uc)
        ),
        compareOnly: [...compareUsecases].filter(
          (uc) => !currentUsecases.has(uc)
        ),
        flowType,
        compareHasOtherFlowData,
        otherFlowUsecases,
      };
    }

    return null;
  }, [
    crossStageEnabled,
    compareStage,
    currentStage,
    isCbe,
    isAccountingGroup,
    filteredObjects,
    crossStageData.cbes,
    crossStageData.events,
    crossStageData.rawCbes,
    crossStageRawData,
    crossStageLoading,
  ]);
};
